import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  XMarkIcon,
  SwatchIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ShoppingBagIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { obtenerConfiguracionTienda, guardarConfiguracionTienda } from '../api/backend_v2';

const PALETAS_PREDEFINIDAS = [
  {
    nombre: 'Oscuro Óptico (Por defecto)',
    color_fondo_principal: '#020617',
    color_fondo_tarjetas: '#0f172a',
    color_botones: '#0891b2',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Azul Noche & Real',
    color_fondo_principal: '#0b1120',
    color_fondo_tarjetas: '#1e293b',
    color_botones: '#2563eb',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Grafito & Esmeralda',
    color_fondo_principal: '#18181b',
    color_fondo_tarjetas: '#27272a',
    color_botones: '#059669',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Carbón & Violeta',
    color_fondo_principal: '#090d16',
    color_fondo_tarjetas: '#171923',
    color_botones: '#7c3aed',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Obsidiana & Ámbar',
    color_fondo_principal: '#0c0a09',
    color_fondo_tarjetas: '#1c1917',
    color_botones: '#d97706',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Marino Profundo & Turquesa',
    color_fondo_principal: '#042f2e',
    color_fondo_tarjetas: '#134e4a',
    color_botones: '#14b8a6',
    color_texto_botones: '#042f2e'
  }
];

export default function TiendaDisenoModal({
  isOpen,
  onClose,
  tiendaData,
  storefrontUrl,
  onSaved
}) {
  const [form, setForm] = useState({
    color_fondo_principal: '#020617',
    color_fondo_tarjetas: '#0f172a',
    color_botones: '#0891b2',
    color_texto_botones: '#ffffff',
    subtitulo_hero:
      'Elegí tus armazones favoritos con descuento exclusivo en efectivo o transferencia. Retirá en nuestra sucursal o recibilo en tu domicilio.'
  });
  const [infoOptica, setInfoOptica] = useState(tiendaData || null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (tiendaData?.tienda_config) {
      setInfoOptica(tiendaData);
      setForm({
        color_fondo_principal: tiendaData.tienda_config.color_fondo_principal || '#020617',
        color_fondo_tarjetas: tiendaData.tienda_config.color_fondo_tarjetas || '#0f172a',
        color_botones: tiendaData.tienda_config.color_botones || '#0891b2',
        color_texto_botones: tiendaData.tienda_config.color_texto_botones || '#ffffff',
        subtitulo_hero:
          tiendaData.tienda_config.subtitulo_hero ||
          'Elegí tus armazones favoritos con descuento exclusivo en efectivo o transferencia. Retirá en nuestra sucursal o recibilo en tu domicilio.'
      });
    } else {
      // Si no se pasaron datos por prop, cargar desde el backend automáticamente
      setCargando(true);
      obtenerConfiguracionTienda()
        .then((data) => {
          if (data) {
            setInfoOptica(data);
            if (data.tienda_config) {
              setForm({
                color_fondo_principal: data.tienda_config.color_fondo_principal || '#020617',
                color_fondo_tarjetas: data.tienda_config.color_fondo_tarjetas || '#0f172a',
                color_botones: data.tienda_config.color_botones || '#0891b2',
                color_texto_botones: data.tienda_config.color_texto_botones || '#ffffff',
                subtitulo_hero:
                  data.tienda_config.subtitulo_hero ||
                  'Elegí tus armazones favoritos con descuento exclusivo en efectivo o transferencia. Retirá en nuestra sucursal o recibilo en tu domicilio.'
              });
            }
          }
        })
        .catch((err) => {
          console.error('Error cargando configuración de tienda:', err);
        })
        .finally(() => {
          setCargando(false);
        });
    }
  }, [isOpen, tiendaData]);

  if (!isOpen) return null;

  const storeBaseUrl = process.env.REACT_APP_STOREFRONT_URL || 'http://localhost:3002';
  const slugOptica = infoOptica?.slug || tiendaData?.slug || '';
  const urlFinalTienda = storefrontUrl || (slugOptica ? `${storeBaseUrl}/${slugOptica}` : storeBaseUrl);

  const aplicarPaleta = (paleta) => {
    setForm((prev) => ({
      ...prev,
      color_fondo_principal: paleta.color_fondo_principal,
      color_fondo_tarjetas: paleta.color_fondo_tarjetas,
      color_botones: paleta.color_botones,
      color_texto_botones: paleta.color_texto_botones
    }));
  };

  const handleCopiarLink = () => {
    navigator.clipboard.writeText(urlFinalTienda);
    Swal.fire({
      icon: 'success',
      title: 'Enlace copiado',
      text: `Se copió ${urlFinalTienda} al portapapeles.`,
      timer: 1500,
      showConfirmButton: false
    });
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const res = await guardarConfiguracionTienda(form);
      if (onSaved) onSaved(res.tienda_config);
      Swal.fire({
        icon: 'success',
        title: 'Diseño de Tienda Actualizado',
        text: 'Los nuevos colores de fondo y botones ya están activos en tu tienda online.',
        timer: 1800,
        showConfirmButton: false
      });
      onClose();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar diseño',
        text: err.message
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget && !guardando) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col my-auto max-h-[90vh] sm:max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-900 via-blue-900 to-slate-900 text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-white/10">
              <SwatchIcon className="w-6 h-6 text-cyan-300" />
            </div>
            <div>
              <h3 className="text-base font-bold">
                Personalización Visual y Enlace de tu Tienda Online
              </h3>
              <p className="text-xs text-blue-200">
                Configurá los colores de fondo, tarjetas y botones de compra de tu vidriera web
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {cargando ? (
          <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-500 flex-1">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-indigo-600" />
            <p className="text-sm font-medium">Cargando configuración visual...</p>
          </div>
        ) : (
          <form onSubmit={handleGuardar} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="p-5 sm:p-6 space-y-6 overflow-y-auto overscroll-contain flex-1">
              {/* Bloque de Enlace Directo a la Tienda */}
              <div className="bg-slate-50 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 block">
                    Link Directo a tu Tienda Online
                  </span>
                  <p className="text-xs text-slate-600 dark:text-slate-300 font-mono bg-white dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 inline-block break-all">
                    {urlFinalTienda}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleCopiarLink}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-xs font-bold transition-colors"
                  >
                    <ClipboardDocumentCheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span>Copiar Link</span>
                  </button>
                  <a
                    href={urlFinalTienda}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md transition-colors"
                  >
                    <span>Abrir Tienda</span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Paletas Predefinidas */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-2.5">
                  1. Elegir una Paleta Rápida (1 Clic)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {PALETAS_PREDEFINIDAS.map((pal) => {
                    const activa =
                      form.color_fondo_principal.toLowerCase() === pal.color_fondo_principal.toLowerCase() &&
                      form.color_botones.toLowerCase() === pal.color_botones.toLowerCase();

                    return (
                      <button
                        key={pal.nombre}
                        type="button"
                        onClick={() => aplicarPaleta(pal)}
                        className={`p-3 rounded-xl border text-left transition-all flex items-center justify-between gap-2 ${
                          activa
                            ? 'border-indigo-600 bg-indigo-50/60 dark:bg-indigo-950/40 ring-1 ring-indigo-500'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 bg-white dark:bg-slate-900/50'
                        }`}
                      >
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                          {pal.nombre}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          <span
                            className="w-4 h-4 rounded-full border border-white/20 shadow-sm"
                            style={{ backgroundColor: pal.color_fondo_principal }}
                            title="Fondo"
                          />
                          <span
                            className="w-4 h-4 rounded-full border border-white/20 shadow-sm"
                            style={{ backgroundColor: pal.color_fondo_tarjetas }}
                            title="Tarjetas"
                          />
                          <span
                            className="w-4 h-4 rounded-full border border-white/20 shadow-sm"
                            style={{ backgroundColor: pal.color_botones }}
                            title="Botones"
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selectores de Color + Vista Previa en Vivo */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Columna Izquierda: Controles de Color */}
                <div className="space-y-4 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
                    2. Ajuste Fino de Colores
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                        Color de Fondo General
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.color_fondo_principal}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_principal: e.target.value }))
                          }
                          className="w-10 h-9 rounded-lg cursor-pointer border border-slate-300 dark:border-slate-600 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_fondo_principal}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_principal: e.target.value }))
                          }
                          className="flex-1 px-2.5 py-1.5 text-xs font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                        Fondo de Tarjetas / Header
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.color_fondo_tarjetas}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_tarjetas: e.target.value }))
                          }
                          className="w-10 h-9 rounded-lg cursor-pointer border border-slate-300 dark:border-slate-600 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_fondo_tarjetas}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_tarjetas: e.target.value }))
                          }
                          className="flex-1 px-2.5 py-1.5 text-xs font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                        Color de Botones Principales
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.color_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_botones: e.target.value }))
                          }
                          className="w-10 h-9 rounded-lg cursor-pointer border border-slate-300 dark:border-slate-600 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_botones: e.target.value }))
                          }
                          className="flex-1 px-2.5 py-1.5 text-xs font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                        Texto de los Botones
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.color_texto_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_texto_botones: e.target.value }))
                          }
                          className="w-10 h-9 rounded-lg cursor-pointer border border-slate-300 dark:border-slate-600 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_texto_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_texto_botones: e.target.value }))
                          }
                          className="flex-1 px-2.5 py-1.5 text-xs font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Mensaje de Bienvenida en Portada
                    </label>
                    <textarea
                      rows={2}
                      value={form.subtitulo_hero}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, subtitulo_hero: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100"
                    />
                  </div>
                </div>

                {/* Columna Derecha: Vista Previa en Vivo */}
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
                    Vista Previa en Vivo
                  </span>
                  <div
                    className="rounded-2xl p-5 border border-slate-700 shadow-inner transition-colors"
                    style={{ backgroundColor: form.color_fondo_principal }}
                  >
                    {/* Mini Header Preview */}
                    <div
                      className="rounded-xl p-3 mb-4 border border-white/10 flex items-center justify-between"
                      style={{ backgroundColor: form.color_fondo_tarjetas }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs"
                          style={{
                            backgroundColor: form.color_botones,
                            color: form.color_texto_botones
                          }}
                        >
                          <SparklesIcon className="w-4 h-4" />
                        </div>
                        <span className="text-xs font-extrabold text-white">
                          {infoOptica?.nombre || tiendaData?.nombre || 'Tu Óptica Online'}
                        </span>
                      </div>
                      <span
                        className="px-2.5 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1"
                        style={{
                          backgroundColor: form.color_botones,
                          color: form.color_texto_botones
                        }}
                      >
                        <ShoppingBagIcon className="w-3.5 h-3.5" /> Carrito
                      </span>
                    </div>

                    {/* Mini Product Card Preview */}
                    <div
                      className="rounded-xl p-4 border border-white/10 flex items-center justify-between gap-3"
                      style={{ backgroundColor: form.color_fondo_tarjetas }}
                    >
                      <div>
                        <span className="text-[10px] uppercase font-bold text-cyan-400 block">
                          VULK • RECETA
                        </span>
                        <span className="text-sm font-bold text-white block">
                          Armazón Modelo Preview
                        </span>
                        <span className="text-base font-black text-emerald-400 block mt-1">
                          $118.500
                        </span>
                      </div>
                      <button
                        type="button"
                        className="px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md"
                        style={{
                          backgroundColor: form.color_botones,
                          color: form.color_texto_botones
                        }}
                      >
                        <ShoppingBagIcon className="w-4 h-4" />
                        <span>Comprar</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={guardando}
                className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-600/30 disabled:opacity-50 transition-colors"
              >
                {guardando ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>Guardar Colores de la Tienda</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

TiendaDisenoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tiendaData: PropTypes.object,
  storefrontUrl: PropTypes.string,
  onSaved: PropTypes.func
};
